import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({
  baseURL: API,
  withCredentials: true,
});

// §TITLE-TOAST: watch every response that carries a `character.new_titles`
// (or top-level `new_titles`) array and pop a celebratory toast so
// achievements land the instant the server awards them — no polling,
// no dependency on which panel triggered the action.
const _titleToasted = new Set();
function _drainNewTitles(payload) {
  if (!payload || typeof payload !== "object") return;
  const list = payload?.character?.new_titles || payload?.new_titles;
  if (!Array.isArray(list) || list.length === 0) return;
  for (const t of list) {
    const id = t?.id;
    if (!id || _titleToasted.has(id)) continue;
    _titleToasted.add(id);
    toast.success(`✦ Title earned: ${t.name || id}`, { duration: 3800 });
  }
}

// §PERK-UNLOCK-TOAST: same pipeline as titles but per (skill, level)
// key so grinding two skills to the same tier fires two separate toasts.
const _perkToasted = new Set();
function _drainNewPerks(payload) {
  if (!payload || typeof payload !== "object") return;
  const list = payload?.character?.new_perks || payload?.new_perks;
  if (!Array.isArray(list) || list.length === 0) return;
  for (const p of list) {
    const key = `${p?.skill}:${p?.level}`;
    if (!p?.skill || !p?.level || _perkToasted.has(key)) continue;
    _perkToasted.add(key);
    toast.success(
      `✦ Perk unlocked: ${p.name || key} · ${p.effect || ""}`,
      { duration: 4500 }
    );
  }
}
http.interceptors.response.use(
  (r) => {
    try {
      _drainNewTitles(r?.data);
      _drainNewPerks(r?.data);
    } catch { /* silent */ }
    return r;
  },
  (e) => Promise.reject(e),
);

export const api = {
  me: () => http.get("/auth/me").then(r => r.data),
  session: (session_id) => http.post("/auth/session", { session_id }).then(r => r.data),
  logout: () => http.post("/auth/logout").then(r => r.data),
  character: () => http.get("/character").then(r => r.data),
  allocate: (stat, amount = 1) => http.post("/character/allocate", { stat, amount }).then(r => r.data),
  travel: () => http.post("/travel/step").then(r => r.data),
  combat: (action, item_id = null, skill_id = null) => http.post("/combat/action", { action, item_id, skill_id }).then(r => r.data),
  useItem: (item_id) => http.post("/inventory/use", { item_id }).then(r => r.data),
  sellItem: (item_id) => http.post("/inventory/sell", { item_id }).then(r => r.data),
  jobs: () => http.get("/jobs").then(r => r.data),
  setJob: (job_id) => http.post("/jobs/set", { job_id }).then(r => r.data),
  quests: () => http.get("/quests").then(r => r.data),
  attemptQuest: (quest_id) => http.post("/quests/attempt", { quest_id }).then(r => r.data),
  craft: () => http.post("/craft").then(r => r.data),
  // Hub
  hubState: () => http.get("/hub/state").then(r => r.data),
  hubMove: (x, y) => http.post("/hub/move", { x, y }).then(r => r.data),
  hubChat: (text) => http.post("/hub/chat", { text }).then(r => r.data),
  hubSit: () => http.post("/hub/sit").then(r => r.data),
  hubEmote: (emote) => http.post("/hub/emote", { emote }).then(r => r.data),
  eliteBounty: () => http.get("/elite-bounty").then(r => r.data),
  bountyContractToday: () => http.get("/bounty-contract/today").then(r => r.data),
  bountyContractBuy: () => http.post("/bounty-contract/buy").then(r => r.data),
  duelChallenge: (opponent_user_id) => http.post("/duel/challenge", { opponent_user_id }).then(r => r.data),
  duelPending: () => http.get("/duel/pending").then(r => r.data),
  duelAccept: (duel_id) => http.post("/duel/accept", { duel_id }).then(r => r.data),
  duelDecline: (duel_id) => http.post("/duel/decline", { duel_id }).then(r => r.data),
  duelLeaderboard: () => http.get("/duel/leaderboard").then(r => r.data),
  profile: (user_id) => http.get(`/profile/${user_id}`).then(r => r.data),
  friendsPending: () => http.get("/friends/pending").then(r => r.data),
  friendsList: () => http.get("/friends/list").then(r => r.data),
  friendRequest: (target_user_id) => http.post("/friends/request", { target_user_id }).then(r => r.data),
  friendAccept: (req_id) => http.post("/friends/accept", { req_id }).then(r => r.data),
  friendDecline: (req_id) => http.post("/friends/decline", { req_id }).then(r => r.data),
  guildPublic: (guild_id) => http.get(`/guilds/${guild_id}/public`).then(r => r.data),
  guildLogoPresets: () => http.get("/guilds/logo-presets").then(r => r.data),
  guildCreateWithLogo: (name, tag, motto, logo, accent_color) => http.post("/guilds/create", { name, tag, motto, logo, accent_color }).then(r => r.data),
  guildWarDeclare: (against_guild_id) => http.post("/guilds/war/declare", { against_guild_id }).then(r => r.data),
  guildWarActive: () => http.get("/guilds/war/active").then(r => r.data),
  guildWarBoard: () => http.get("/guilds/war/board").then(r => r.data),
  gmSync: (payload) => http.post("/admin/gm/sync", payload).then(r => r.data),
  gmPull: () => http.get("/admin/gm/pull").then(r => r.data),
  gmClasses: () => http.get("/gm/classes").then(r => r.data),
  gmActiveWorldBoss: () => http.get("/gm/active-world-boss").then(r => r.data),
  gmAbilities: () => http.get("/gm/abilities").then(r => r.data),
  gmActivePlayers: () => http.get("/admin/gm/active-players").then(r => r.data),
  gmAssignPlayerClass: (user_id, class_name) => http.post("/admin/gm/players/assign-class", { user_id, class_name }).then(r => r.data),
  // Forge (§8 Fase 2)
  forgeList: () => http.get("/gm/forge").then(r => r.data),
  forgeSet: (itemIds) => http.post("/admin/gm/forge", { itemIds }).then(r => r.data),
  // Elements (§3 Fase 1)
  gmElements: () => http.get("/gm/elements").then(r => r.data),
  gmElementUpsert: (element) => http.post("/admin/gm/elements", element).then(r => r.data),
  gmElementDelete: (id) => http.delete(`/admin/gm/elements/${id}`).then(r => r.data),
  // GM Graphics (image assets stored on-disk under backend/assets/gm_images)
  gmListImages: () => http.get("/admin/gm/images/list").then(r => r.data),
  gmUploadImage: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post("/admin/gm/images/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(r => r.data);
  },
  gmDeleteImage: (filename) => http.delete(`/admin/gm/images/${filename}`).then(r => r.data),
  // GM Item Balance Tester — POST { item, enemy, attacker_level, turns }
  gmSimulateItem: (payload) => http.post("/admin/gm/items/simulate", payload).then(r => r.data),
  // Partners (HUB collab showcase)
  gmListPartners: () => http.get("/admin/gm/partners/all").then(r => r.data),
  gmUpsertPartner: (partner) => http.post("/admin/gm/partners", partner).then(r => r.data),
  gmDeletePartner: (id) => http.delete(`/admin/gm/partners/${id}`).then(r => r.data),
  hubPartners: () => http.get("/gm/partners").then(r => r.data),
  gmTranslateAll: (force = false) => http.post("/admin/gm/translate-all", { force }).then(r => r.data),
  gmTranslateQuests: (force = false) => http.post("/admin/gm/translate-quests", { force }).then(r => r.data),
  gmLanguageStats: () => http.get("/admin/gm/language-stats").then(r => r.data),
  gmLanguageStatsHistory: () => http.get("/admin/gm/language-stats/history").then(r => r.data),
  gmListTranslations: (collection = null) =>
    http.get("/admin/gm/translations", { params: collection ? { collection } : {} }).then(r => r.data),
  gmOverrideTranslation: (collection, entity_id, lang, fields) =>
    http.put("/admin/gm/translations/override",
      { collection, entity_id, lang, fields }).then(r => r.data),
  gmResetTranslationOverride: (collection, entity_id, lang) =>
    http.delete("/admin/gm/translations/override",
      { params: { collection, entity_id, lang } }).then(r => r.data),
  gmSkillLeaderboard: (top_n = 3) =>
    http.get("/admin/gm/skill-leaderboard", { params: { top_n } }).then(r => r.data),
  translateTexts: (texts, lang) => http.post("/character/translate-texts", { texts, lang }).then(r => r.data),
  // Admin player patch (§2)
  gmPatchPlayer: (payload) => http.post("/admin/gm/players/patch", payload).then(r => r.data),
  gmPlayersList: () => http.get("/admin/gm/players").then(r => r.data),
  gmPlayerDetail: (user_id) => http.get(`/admin/gm/players/${user_id}/detail`).then(r => r.data),
  gmPlayerEquip: (user_id, item_id) => http.post("/admin/gm/players/equip", { user_id, item_id }).then(r => r.data),
  gmPlayerUnequip: (user_id, slot) => http.post("/admin/gm/players/unequip", { user_id, slot }).then(r => r.data),
  gmPlayerSetSkill: (user_id, skill, level, xp) => http.post("/admin/gm/players/skills/set", { user_id, skill, level, xp }).then(r => r.data),
  gmPlayerGrantItem: (user_id, gm_item_id, quantity = 1) => http.post("/admin/gm/players/inventory/grant", { user_id, gm_item_id, quantity }).then(r => r.data),
  gmPlayerRemoveItem: (user_id, item_id) => http.post("/admin/gm/players/inventory/remove", { user_id, item_id }).then(r => r.data),
  gmItems: () => http.get("/gm/items").then(r => r.data),
  // Admin role management (§8)
  gmSetAdmin: (user_id, is_admin) => http.post("/admin/gm/users/set-admin", { user_id, is_admin }).then(r => r.data),
  // Settings (§6 accessibility) & Pre-Alpha toggle (§9)
  patchPreferences: (payload) => http.patch("/character/preferences", payload).then(r => r.data),
  togglePreAlphaTitle: (show_title) => http.post("/character/pre-alpha/visibility", { show_title }).then(r => r.data),
  // Sponsorship (§4)
  sponsorshipSubmit: (payload) => http.post("/gm/sponsorship-request", payload).then(r => r.data),
  sponsorshipList: () => http.get("/admin/gm/sponsorship-requests").then(r => r.data),
  sponsorshipSetStatus: (id, status) => http.post("/admin/gm/sponsorship-requests/status", { id, status }).then(r => r.data),
  // Discord admin module (all endpoints under /api/admin/discord/*)
  discordStatus: () => http.get("/admin/discord/status").then(r => r.data),
  discordPreview: (payload) => http.post("/admin/discord/preview", { payload }).then(r => r.data),
  discordTest: () => http.post("/admin/discord/test").then(r => r.data),
  discordSend: (payload, draft_id = null) => http.post("/admin/discord/send", { payload, draft_id }).then(r => r.data),
  discordHistory: (limit = 50) => http.get(`/admin/discord/history?limit=${limit}`).then(r => r.data),
  discordDrafts: () => http.get("/admin/discord/drafts").then(r => r.data),
  discordDraftCreate: (payload) => http.post("/admin/discord/drafts", { payload }).then(r => r.data),
  discordDraftUpdate: (draft_id, payload) => http.put(`/admin/discord/drafts/${draft_id}`, { payload }).then(r => r.data),
  discordDraftDelete: (draft_id) => http.delete(`/admin/discord/drafts/${draft_id}`).then(r => r.data),
  discordDraftDuplicate: (draft_id) => http.post(`/admin/discord/drafts/${draft_id}/duplicate`).then(r => r.data),
  // Market
  market: () => http.get("/market").then(r => r.data),
  marketList: (item_id, price) => http.post("/market/list", { item_id, price }).then(r => r.data),
  marketBuy: (listing_id) => http.post("/market/buy", { listing_id }).then(r => r.data),
  marketCancel: (listing_id) => http.post("/market/cancel", { listing_id }).then(r => r.data),
  dismantleItem: (item_id) => http.post("/inventory/dismantle", { item_id }).then(r => r.data),
  // Guilds
  guilds: () => http.get("/guilds").then(r => r.data),
  guildMine: () => http.get("/guilds/mine").then(r => r.data),
  guildCreate: (name, tag, motto) => http.post("/guilds/create", { name, tag, motto }).then(r => r.data),
  guildJoin: (guild_id) => http.post("/guilds/join", { guild_id }).then(r => r.data),
  guildLeave: () => http.post("/guilds/leave").then(r => r.data),
  // World Boss
  worldBoss: () => http.get("/worldboss").then(r => r.data),
  worldBossHit: (action = "attack") => http.post("/worldboss/hit", { action }).then(r => r.data),
  // Recruit
  recruitPosts: () => http.get("/recruit/posts").then(r => r.data),
  recruitPost: (message) => http.post("/recruit/post", { message }).then(r => r.data),
  recruitApply: (post_id) => http.post("/recruit/apply", { post_id }).then(r => r.data),
  recruitDecide: (post_id, applicant_id, accept) => http.post("/recruit/decide", { post_id, applicant_id, accept }).then(r => r.data),
  recruitClose: (post_id) => http.post("/recruit/close", { post_id }).then(r => r.data),
  // Arena
  arenaOpponents: () => http.get("/arena/opponents").then(r => r.data),
  arenaDuel: (wager, target_id) => http.post("/arena/duel", { wager, target_id }).then(r => r.data),
  arenaHistory: () => http.get("/arena/history").then(r => r.data),
  // Leaderboards
  leaderboards: () => http.get("/leaderboards").then(r => r.data),
  // Cosmetics
  cosmetics: () => http.get("/cosmetics").then(r => r.data),
  cosmeticsBuy: (item_id) => http.post("/cosmetics/buy", { item_id }).then(r => r.data),
  cosmeticsEquip: (item_id) => http.post("/cosmetics/equip", { item_id }).then(r => r.data),
  // Daily
  dailyTasks: () => http.get("/daily/tasks").then(r => r.data),
  dailyClaim: (task_id) => http.post("/daily/claim", { task_id }).then(r => r.data),
  // Party
  partyMine: () => http.get("/party/mine").then(r => r.data),
  partyOpen: () => http.get("/party/open").then(r => r.data),
  partyCreate: () => http.post("/party/create").then(r => r.data),
  partyJoin: (party_id) => http.post("/party/join", { party_id }).then(r => r.data),
  partyLeave: () => http.post("/party/leave").then(r => r.data),
  // Skill perks
  skillPerks: () => http.get("/skills/perks").then(r => r.data),
  // Emotes
  hubEmotes: () => http.get("/hub/emotes").then(r => r.data),
  // Events
  eventCurrent: () => http.get("/events/current").then(r => r.data),
  // Titles
  titles: () => http.get("/titles").then(r => r.data),
  titleEquip: (title_id) => http.post("/titles/equip", { title_id }).then(r => r.data),
  // Guild vault
  vaultDepositGold: (amount) => http.post("/guilds/vault/deposit-gold", { amount }).then(r => r.data),
  vaultWithdrawGold: (amount) => http.post("/guilds/vault/withdraw-gold", { amount }).then(r => r.data),
  vaultDepositItem: (item_id) => http.post("/guilds/vault/deposit-item", { item_id }).then(r => r.data),
  vaultWithdrawItem: (item_id) => http.post("/guilds/vault/withdraw-item", { item_id }).then(r => r.data),
  guildUpgrade: () => http.post("/guilds/upgrade").then(r => r.data),
  // Global chat
  chatGlobal: () => http.get("/chat/global").then(r => r.data),
  chatGlobalSend: (text) => http.post("/chat/global", { text }).then(r => r.data),
  // Notifications
  notifications: () => http.get("/notifications").then(r => r.data),
  notificationsRead: (ids = null) => http.post("/notifications/read", ids ? { ids } : { all: true }).then(r => r.data),
  // Feed
  feed: () => http.get("/feed").then(r => r.data),
  // Referrals
  referralsMe: () => http.get("/referrals/me").then(r => r.data),
  referralsApply: (code) => http.post("/referrals/apply", { code }).then(r => r.data),
  // Guild chat
  guildChatGet: () => http.get("/guilds/chat").then(r => r.data),
  guildChatSend: (text) => http.post("/guilds/chat", { text }).then(r => r.data),
  // Season pass
  season: () => http.get("/season").then(r => r.data),
  seasonClaim: (tier) => http.post("/season/claim", { tier }).then(r => r.data),
  // Equipment
  equip: (item_id) => http.post("/inventory/equip", { item_id }).then(r => r.data),
  unequip: (slot) => http.post("/inventory/unequip", { slot }).then(r => r.data),
  autoEquip: () => http.post("/inventory/auto-equip").then(r => r.data),
  fuse: (item_a, item_b) => http.post("/inventory/fuse", { item_a, item_b }).then(r => r.data),
  fusePreview: (item_a, item_b) => http.get(`/inventory/fuse-preview?item_a=${item_a}&item_b=${item_b}`).then(r => r.data),
  enchant: (item_id) => http.post("/inventory/enchant", { item_id }).then(r => r.data),
  resetStats: () => http.post("/character/reset-stats").then(r => r.data),
  // Contract, profile, fast travel
  guildContract: () => http.get("/guilds/contract").then(r => r.data),
  guildContractClaim: () => http.post("/guilds/contract/claim").then(r => r.data),
  guildContractHistory: () => http.get("/guilds/contract/history").then(r => r.data),
  profile: () => http.get("/profile").then(r => r.data),
  travelFast: () => http.post("/travel/step", { skip_cooldown: true }).then(r => r.data),
  // Onboarding
  onboardCheckName: (name, chosen_class = "Warrior") => http.post("/onboard/check-name", { name, chosen_class }).then(r => r.data),
  onboard: (name, chosen_class, appearance) => http.post("/onboard", { name, chosen_class, appearance }).then(r => r.data),
  // Minigame
  minigameCurrent: () => http.get("/minigame/current").then(r => r.data),
  minigameResolve: (session_id, hit) => http.post("/minigame/resolve", { session_id, hit }).then(r => r.data),

  // Moderation & tickets
  moderationMe: () => http.get("/moderation/me").then(r => r.data),
  ticketOpen: (subject, body) => http.post("/tickets", { subject, body }).then(r => r.data),
  ticketsMine: () => http.get("/tickets/mine").then(r => r.data),
  ticketReply: (id, body) => http.post(`/tickets/${id}/reply`, { body }).then(r => r.data),
  adminTicketsList: () => http.get("/admin/tickets").then(r => r.data),
  adminTicketClose: (id) => http.post(`/admin/tickets/${id}/close`).then(r => r.data),
  adminMute: (user_id, minutes, reason) => http.post("/admin/mute", { user_id, minutes, reason }).then(r => r.data),
  adminUnmute: (user_id) => http.post("/admin/unmute", { user_id }).then(r => r.data),
  adminModActions: (limit = 100) => http.get(`/admin/mod-actions?limit=${limit}`).then(r => r.data),
  adminClearChat: (channel) => http.post("/admin/chat/clear", { channel }).then(r => r.data),
  modlogSettings:       () => http.get("/admin/modlog/settings").then(r => r.data),
  modlogSetSettings:    (ai_forward_enabled) => http.put("/admin/modlog/settings", { ai_forward_enabled }).then(r => r.data),
  modlogBackfill:       (limit = 50) => http.post(`/admin/modlog/backfill?limit=${limit}`).then(r => r.data),
  modlogBackfillHistory: (limit = 20) => http.get(`/admin/modlog/backfill/history?limit=${limit}`).then(r => r.data),
  adminUsers: () => http.get("/admin/users").then(r => r.data),
  adminDeleteUser: (user_id) => http.delete(`/admin/users/${user_id}`).then(r => r.data),
  // Class skills & biology
  classSkills: () => http.get("/class-skills").then(r => r.data),
  biologyRecipes: () => http.get("/biology/recipes").then(r => r.data),
  biologyCraft: (recipe_id) => http.post("/biology/craft", { recipe_id }).then(r => r.data),
  // Character preferences (combat log verbosity, visual FX toggles).
  setPreferences: (patch) => http.patch("/character/preferences", patch).then(r => r.data),
  // Combat elements + interaction matrix (public read-only).
  combatElements: () => http.get("/combat/elements").then(r => r.data),
  // Admin: Shadow → Darkness migration
  adminMigrateElements: () => http.post("/admin/combat/migrate").then(r => r.data),
  // Forging + Enchanting (Session 4)
  forgeCatalog: (category) => http.get("/forge/catalog", { params: category ? { category } : {} }).then(r => r.data),
  forgePreview: (rarity, base_id, locked_count = 0) =>
    http.post("/forge/preview", { rarity, base_id, locked_count }).then(r => r.data),
  forge: (rarity, base_id, locked_count = 0) =>
    http.post("/forge", { rarity, base_id, locked_count }).then(r => r.data),
  enchantPreview: (item_id, locked_stats = []) =>
    http.post("/enchant/preview", { item_id, locked_stats }).then(r => r.data),
  enchant: (item_id, locked_stats = []) =>
    http.post("/inventory/enchant", { item_id, locked_stats }).then(r => r.data),
  // Offline claim box (Session 6)
  claimOffline: (nonce) => http.post("/claim/rewards", { nonce }).then(r => r.data),
  // Codex (Session 8)
  codexHome:     () => http.get("/codex").then(r => r.data),
  codexEnemy:    (id) => http.get(`/codex/enemy/${encodeURIComponent(id)}`).then(r => r.data),
  codexElements: () => http.get("/codex/elements").then(r => r.data),
  codexHunts:    () => http.get("/codex/hunts").then(r => r.data),
  codexHuntAccept: (key) => http.post("/codex/hunts/accept", { key }).then(r => r.data),
  codexHuntAbandon: () => http.post("/codex/hunts/abandon").then(r => r.data),
  codexHuntReroll: () => http.post("/codex/hunts/reroll").then(r => r.data),
  updateTravelVision: (payload) => http.put("/character/travel-vision", payload).then(r => r.data),
  // Character appearance (Session 9 — heterochromia + late-game edits)
  patchAppearance: (patch) => http.patch("/character/appearance", patch).then(r => r.data),
  // GM Damage Calculator (Session 9)
  gmDamageCalc: (payload) => http.post("/admin/gm/damage-calc", payload).then(r => r.data),
};
